import React, { useEffect, useState, useRef } from 'react';
import PdfUploadModal from './PdfUploadModal';
import TaskRows from './TaskRows';

// Adapter: courseCreateStreamReducer's `stages` model -> TaskRows' generic
// `tasks` prop. Status maps 1:1. `amount` mirrors the per-stage truth table
// in docs/sse-course-creation-api-contract.md (`completed`/`total` are
// ABSENT, never 0/null, when a stage can't report real progress). No stage
// ever fails independently today — a stage failure ends the whole run and
// StatusModal shows the separate 'error' state — so this never emits a
// 'failed' task, but TaskRows itself supports it for future multi-task use.
function stagesToTasks(stages = []) {
  return stages.map((stage) => {
    let amount;
    if (typeof stage.completed === 'number' && typeof stage.total === 'number') {
      amount = `${stage.completed}/${stage.total}`;
    } else if (typeof stage.total === 'number') {
      amount = `${stage.total} lesson${stage.total === 1 ? '' : 's'}`;
    }
    return {
      key: stage.key,
      label: stage.label,
      status: stage.status,
      amount,
      pill: stage.status === 'done' ? 'Completed' : undefined,
    };
  });
}

function StatusModal({
  status,
  errorMessage,
  onRetry,
  onDismiss,
  stages,
  onCancel,
  successSubtitle,
  connectionLostHref,
  connectionLostLabel,
}) {
  return (
    <div className="thinking-overlay">
      <div className={`thinking-modal ${status}`}>
        {status === 'loading' && (
          <>
            <TaskRows tasks={stagesToTasks(stages)} />
            <button type="button" className="error-btn secondary" onClick={onCancel}>
              Cancel
            </button>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="success-icon">
              <svg className="checkmark" viewBox="0 0 52 52">
                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
              </svg>
            </div>
            <div className="thinking-text-container">
              <span className="thinking-word success-text">Course Created!</span>
            </div>
            <p className="thinking-subtitle">{successSubtitle}</p>
          </>
        )}

        {status === 'connection_lost' && (
          <>
            <div className="warning-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            </div>
            <div className="thinking-text-container">
              <span className="thinking-word warning-text">Connection lost</span>
            </div>
            <p className="error-message">
              {errorMessage || 'Your course may still be finishing. Check your dashboard.'}
            </p>
            <div className="error-actions">
              <a href={connectionLostHref} className="error-btn primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                {connectionLostLabel}
              </a>
            </div>
          </>
        )}

        {status === 'content_warning' && (
          <>
            <div className="warning-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <rect x="11" y="9" width="2" height="5" rx="1" fill="currentColor" stroke="none" />
                <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
              </svg>
            </div>
            <div className="thinking-text-container">
              <span className="thinking-word warning-text">Content Warning</span>
            </div>
            <p className="error-message">{errorMessage || 'This topic cannot be processed. Please try a different topic.'}</p>
            <div className="error-actions">
              <button type="button" className="error-btn primary" onClick={onDismiss}>
                Try Different Topic
              </button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="error-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="thinking-text-container">
              <span className="thinking-word error-text">Something went wrong</span>
            </div>
            <p className="error-message">{errorMessage || 'Failed to create course. Please try again.'}</p>
            <div className="error-actions">
              <button type="button" className="error-btn secondary" onClick={onDismiss}>
                Cancel
              </button>
              <button type="button" className="error-btn primary" onClick={onRetry}>
                Try Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const DIFFICULTIES = [
  { value: '', label: 'Auto-detect' },
  { value: 'BEGINNER', label: 'Beginner' },
  { value: 'INTERMEDIATE', label: 'Intermediate' },
  { value: 'ADVANCED', label: 'Advanced' },
];

export default function CourseRequestForm({
  onSubmit,
  status = 'idle', // 'idle' | 'loading' | 'success' | 'content_warning' | 'error' | 'connection_lost'
  errorMessage = '',
  onRetry,
  onDismiss,
  // Streaming create (see useCreateCourseStream). `stages` drives the
  // TaskRows list (via stagesToTasks) during 'loading'; `onCancel` aborts
  // the in-flight stream.
  stages,
  onCancel,
  successSubtitle = 'Taking you to your new course...',
  connectionLostHref = '/dashboard',
  connectionLostLabel = 'Go to dashboard',
  // Parent-portal extensions (all optional; self-serve flow is unchanged when omitted).
  heroTitle = 'What do you want to learn?',
  heroSubtitle = "Enter a topic and we'll create a personalized course for you",
  studentId,
  onPdfSuccessNavigate,
  onPdfError,
}) {
  const isSubmitting = status === 'loading' || status === 'success' || status === 'connection_lost';
  const [title, setTitle] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [difficulty, setDifficulty] = useState('');
  const [courseDomain, setCourseDomain] = useState('');
  const [expectedOutcomes, setExpectedOutcomes] = useState('');
  const [prerequisiteKnowledge, setPrerequisiteKnowledge] = useState('');
  const [difficultyOpen, setDifficultyOpen] = useState(false);

  const difficultyRefDesktop = useRef(null);
  const difficultyRefMobile = useRef(null);
  const inputRef = useRef(null);

  // Close difficulty dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is outside BOTH desktop and mobile dropdowns
      const isInsideDesktop = difficultyRefDesktop.current?.contains(e.target);
      const isInsideMobile = difficultyRefMobile.current?.contains(e.target);
      if (!isInsideDesktop && !isInsideMobile) {
        setDifficultyOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setDifficultyOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting || !title.trim()) return;

    const advancedSettings = {};
    if (difficulty) advancedSettings.difficulty = difficulty;
    if (courseDomain.trim()) advancedSettings.courseDomain = courseDomain.trim();
    if (expectedOutcomes.trim()) advancedSettings.expectedOutcomes = expectedOutcomes.trim();
    if (prerequisiteKnowledge.trim()) advancedSettings.prerequisiteKnowledge = prerequisiteKnowledge.trim();

    onSubmit({
      title: title.trim(),
      advancedSettings: Object.keys(advancedSettings).length > 0 ? advancedSettings : undefined,
    });
  };

  // The topic field is a textarea so it grows vertically with long topics
  // instead of scrolling on one line. Reset to auto first so it can also shrink.
  const autoSizeTopic = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    autoSizeTopic(e.target);
  };

  // Preserve the old <input> behavior: Enter submits, Shift+Enter adds a newline.
  const handleTopicKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };

  // Size the topic field on mount and whenever the value is set programmatically
  // (e.g. a prefilled title), not just on keystrokes.
  useEffect(() => {
    autoSizeTopic(inputRef.current);
  }, [title]);

  const selectedDifficulty = DIFFICULTIES.find(d => d.value === difficulty);

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        /* Thinking Modal Overlay */
        .thinking-overlay {
          position: fixed;
          inset: 0;
          background: var(--overlay-bg);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease;
        }

        .thinking-modal {
          background: var(--bg-elevated);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-overlay);
          padding: var(--space-6) var(--space-7);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-5);
          box-shadow: var(--shadow-lg);
          animation: scaleIn 0.3s ease;
        }

        /* The stepper needs room for four full-width rows — wider than the
           other (icon + one-liner) states. */
        .thinking-modal.loading {
          min-width: 300px;
        }

        .thinking-text-container {
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .thinking-word {
          font-size: var(--text-page-size);
          font-weight: var(--text-page-weight);
          letter-spacing: var(--text-page-tracking);
          color: var(--text-primary);
          transition: opacity 0.2s ease, transform 0.2s ease;
        }

        .thinking-subtitle {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0;
          animation: pulse 2s ease-in-out infinite;
        }

        /* Success State — completion, so green is legitimate (spec §2). */
        .success-icon {
          width: 64px;
          height: 64px;
        }

        .checkmark {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          display: block;
          stroke-width: 2;
          stroke: var(--success);
          stroke-miterlimit: 10;
          animation: checkFill 0.4s ease-in-out 0.4s forwards, checkScale 0.3s ease-in-out 0.9s both;
        }

        .checkmark-circle {
          stroke-dasharray: 166;
          stroke-dashoffset: 166;
          stroke-width: 2;
          stroke-miterlimit: 10;
          stroke: var(--success);
          fill: none;
          animation: checkStroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
        }

        .checkmark-check {
          transform-origin: 50% 50%;
          stroke-dasharray: 48;
          stroke-dashoffset: 48;
          stroke-width: 3;
          animation: checkStroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
        }

        @keyframes checkStroke {
          100% { stroke-dashoffset: 0; }
        }

        @keyframes checkScale {
          0%, 100% { transform: none; }
          50% { transform: scale3d(1.1, 1.1, 1); }
        }

        @keyframes checkFill {
          100% { box-shadow: inset 0 0 0 100px var(--success-bg); }
        }

        .success-text {
          color: var(--success) !important;
        }

        .thinking-modal.success .thinking-subtitle {
          animation: none;
        }

        /* Warning State (Content Policy) */
        .warning-icon {
          width: 64px;
          height: 64px;
          color: var(--warning);
          animation: warningPulse 0.6s ease-in-out;
        }

        .warning-icon svg {
          width: 100%;
          height: 100%;
        }

        @keyframes warningPulse {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }

        .warning-text {
          color: var(--warning) !important;
        }

        .thinking-modal.content_warning .thinking-subtitle {
          animation: none;
        }

        /* Error State */
        .error-icon {
          width: 64px;
          height: 64px;
          color: var(--danger);
          animation: errorShake 0.5s ease-in-out;
        }

        .error-icon svg {
          width: 100%;
          height: 100%;
        }

        @keyframes errorShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }

        .error-text {
          color: var(--danger) !important;
        }

        .error-message {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0;
          text-align: center;
          max-width: 280px;
          line-height: 1.5;
        }

        .thinking-modal.error .thinking-subtitle {
          animation: none;
        }

        .error-actions {
          display: flex;
          gap: var(--space-3);
          margin-top: var(--space-2);
        }

        /* Matches the Button.jsx primitive's md size + variant tokens so
           these one-off status-modal buttons read like every other button
           in the app. */
        .error-btn {
          padding: var(--space-3) var(--space-4);
          border-radius: var(--radius-control);
          font-size: var(--text-body-size);
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
        }

        .error-btn.secondary {
          background: var(--btn-secondary-bg);
          border: 1px solid var(--btn-secondary-border);
          color: var(--btn-secondary-text);
        }

        .error-btn.secondary:hover {
          background: var(--btn-secondary-hover);
        }

        .error-btn.primary {
          background: var(--btn-primary-bg);
          border: 1px solid transparent;
          color: var(--btn-primary-text);
        }

        .error-btn.primary:hover {
          background: var(--btn-primary-hover);
        }

        .error-btn:focus-visible {
          outline: none;
          box-shadow: var(--focus-ring);
        }

        .course-form-container {
          min-height: calc(100svh - var(--topbar-h) - 48px);
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: var(--space-5) 0;
        }

        .course-form-inner {
          width: 100%;
          max-width: 640px;
          margin: 0 auto;
        }

        .course-form-header {
          text-align: center;
          margin-bottom: var(--space-6);
        }

        /* Hero heading, but still built from the shared type scale rather
           than a bespoke oversized display weight. */
        .course-form-title {
          font-size: clamp(22px, 4vw, var(--text-page-size));
          font-weight: var(--text-page-weight);
          color: var(--text-primary);
          letter-spacing: var(--text-page-tracking);
          line-height: 1.25;
          margin: 0;
        }

        .course-form-subtitle {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          line-height: 1.5;
          margin: var(--space-2) 0 0;
        }

        .main-input-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-panel);
          padding: var(--space-2);
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        /* A thin 1px ring + soft neutral lift reads as "focused" without the
           heavy 3px --focus-ring halo, which looks like a thick dark box on
           a card this large (that halo is tuned for small controls). */
        .main-input-card:focus-within {
          border-color: var(--input-focus-border);
          box-shadow: 0 0 0 1px var(--input-focus-border), var(--shadow-md);
        }

        .main-input {
          width: 100%;
          box-sizing: border-box;
          background: transparent !important;
          border: none;
          outline: none;
          color: var(--text-primary);
          font-size: clamp(16px, 3.5vw, 18px);
          font-family: inherit;
          line-height: 1.4;
          padding: var(--space-3) var(--space-3) var(--space-2);
          min-height: 48px;
          /* Textarea that grows with its content (height set via JS) instead of
             scrolling: no manual resize handle, no scrollbar. */
          resize: none;
          overflow: hidden;
          -webkit-appearance: none;
          appearance: none;
        }

        /* Fix Safari/Chrome autofill background */
        .main-input:-webkit-autofill,
        .main-input:-webkit-autofill:hover,
        .main-input:-webkit-autofill:focus,
        .main-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
          -webkit-text-fill-color: var(--text-primary) !important;
          background-color: transparent !important;
          transition: background-color 5000s ease-in-out 0s;
        }

        .main-input:focus {
          background: transparent !important;
          box-shadow: none !important;
        }

        .main-input::placeholder {
          color: var(--input-placeholder);
        }

        /* Submit is this form's one legitimate primary action (spec §2). */
        .submit-btn {
          width: 34px;
          height: 34px;
          border-radius: var(--radius-control);
          border: none;
          background: var(--btn-primary-bg);
          color: var(--btn-primary-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
          flex-shrink: 0;
        }

        .submit-btn:hover:not(:disabled) {
          background: var(--btn-primary-hover);
          transform: scale(1.05);
        }

        .submit-btn:focus-visible {
          outline: none;
          box-shadow: var(--focus-ring);
        }

        .submit-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        /* Consolidated toolbar inside the input card — Advanced ▾ · Attach PDF · Send. */
        .main-input-toolbar {
          display: flex;
          align-items: center;
          gap: var(--space-1);
          padding: var(--space-2) var(--space-2) var(--space-1);
          border-top: 1px solid var(--border-subtle);
        }

        /* Toolbar affordance, not a primary action or status — neutral in
           both its resting and pressed/open states (spec §2). */
        .toolbar-btn {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-2) var(--space-3);
          background: transparent;
          border: none;
          border-radius: var(--radius-control);
          color: var(--text-secondary);
          font-size: 13px;
          font-weight: 500;
          font-family: inherit;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .toolbar-btn:hover:not(:disabled) {
          background: var(--glass-bg-medium);
          color: var(--text-primary);
        }

        .toolbar-btn:focus-visible {
          outline: none;
          box-shadow: var(--focus-ring);
        }

        .toolbar-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .toolbar-btn.is-active {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }

        .toolbar-btn svg {
          width: 15px;
          height: 15px;
          flex-shrink: 0;
        }

        .toolbar-btn .chev {
          transition: transform 0.2s ease;
          opacity: 0.6;
        }

        .toolbar-btn.is-active .chev {
          transform: rotate(180deg);
          opacity: 1;
        }

        .toolbar-spacer {
          flex: 1;
        }


        .advanced-content {
          background: var(--bg-secondary);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-panel);
          padding: 0 var(--space-5);
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                      padding 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                      margin-top 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .advanced-content.open {
          max-height: 600px;
          opacity: 1;
          padding: var(--space-5);
          margin-top: var(--space-2);
        }

        .advanced-grid {
          display: grid;
          gap: var(--space-5);
        }

        .field-group {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }

        /* Form-field caption, matching the LinkStudentModal label
           convention — never the field-level "primary action" treatment. */
        .field-label {
          font-size: var(--text-label-size);
          font-weight: var(--text-label-weight);
          letter-spacing: var(--text-label-tracking);
          text-transform: uppercase;
          color: var(--text-tertiary);
        }

        .field-input,
        .field-textarea {
          width: 100%;
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          border-radius: var(--radius-control);
          padding: var(--space-3) var(--space-4);
          color: var(--text-primary);
          font-size: var(--text-body-size);
          font-family: inherit;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          outline: none;
        }

        .field-input:focus,
        .field-textarea:focus {
          border-color: var(--input-focus-border);
          box-shadow: var(--focus-ring);
        }

        .field-input::placeholder,
        .field-textarea::placeholder {
          color: var(--input-placeholder);
        }

        .field-textarea {
          resize: vertical;
          min-height: 72px;
          line-height: 1.5;
        }

        .field-char-count {
          font-size: var(--text-numeric-size);
          color: var(--text-tertiary);
          text-align: right;
          margin-top: var(--space-1);
          transition: color 0.2s ease;
        }

        .field-char-count.warning {
          color: var(--warning);
        }

        .field-char-count.limit {
          color: var(--danger);
        }

        .difficulty-dropdown {
          position: relative;
        }

        .difficulty-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-3) var(--space-4);
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          border-radius: var(--radius-control);
          color: var(--text-primary);
          font-size: var(--text-body-size);
          cursor: pointer;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .difficulty-trigger:hover {
          border-color: var(--border-hover);
        }

        .difficulty-trigger.open {
          border-color: var(--input-focus-border);
          box-shadow: var(--focus-ring);
        }

        .difficulty-menu {
          position: absolute;
          top: calc(100% + var(--space-2));
          left: 0;
          right: 0;
          background: var(--bg-elevated);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-control);
          padding: var(--space-1);
          box-shadow: var(--shadow-lg);
          z-index: 100;
          opacity: 0;
          transform: translateY(-8px);
          pointer-events: none;
          transition: opacity 0.15s ease, transform 0.15s ease;
        }

        .difficulty-menu.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        .difficulty-option {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          width: 100%;
          padding: var(--space-2) var(--space-3);
          background: transparent;
          border: none;
          border-radius: 6px;
          color: var(--text-primary);
          font-size: var(--text-body-size);
          cursor: pointer;
          transition: background 0.15s ease;
          text-align: left;
        }

        .difficulty-option:hover {
          background: var(--glass-bg-medium);
        }

        /* A selected chip in a menu, not a status — neutral fill, the
           checkmark glyph (not colour alone) carries the selected state. */
        .difficulty-option.selected {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          font-weight: 600;
        }

        .difficulty-check {
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Mobile - bottom-anchored input like chat apps */
        @media (max-width: 768px) {
          .course-form-container {
            min-height: calc(100svh - var(--topbar-h) - 160px); /* Account for topbar and bottom input */
            justify-content: center;
            padding: var(--space-5) var(--space-4);
          }

          .course-form-inner {
            display: contents;
          }

          .course-form-header {
            text-align: center;
            margin-bottom: 0;
          }

          /* Fixed bottom input area */
          .mobile-bottom-input {
            position: fixed;
            bottom: 80px; /* Sit above speed dial */
            left: 0;
            right: 0;
            background: var(--bg-primary);
            border-top: 1px solid var(--border-subtle);
            padding: var(--space-3) var(--space-4);
            padding-bottom: max(12px, env(safe-area-inset-bottom));
            z-index: 100;
          }

          .main-input-card {
            border-radius: var(--radius-panel);
          }

          .main-input {
            font-size: 16px; /* Prevent iOS zoom */
            padding: var(--space-3);
            min-height: 44px;
          }

          .submit-btn {
            width: 34px;
            height: 34px;
          }


          .advanced-content {
            border-radius: var(--radius-panel);
          }

          .advanced-content.open {
            padding: var(--space-4);
            max-height: 50vh;
            overflow-y: auto;
            margin-top: 0;
            margin-bottom: var(--space-2);
          }

          .field-input,
          .field-textarea {
            font-size: 16px; /* Prevent iOS zoom */
          }

          .thinking-modal {
            margin: var(--space-4);
            padding: var(--space-6) var(--space-5);
            width: calc(100% - 32px);
            max-width: 380px;
          }
        }

        /* Hide elements based on mobile/desktop context */
        .desktop-form-area {
          display: block;
        }
        .mobile-bottom-input {
          display: none;
        }

        @media (max-width: 768px) {
          .desktop-form-area {
            display: none;
          }
          .mobile-bottom-input {
            display: block;
          }
        }

      `}</style>

      <div className="course-form-container">
        <div className="course-form-inner">
          <header className="course-form-header">
            <h1 className="course-form-title">{heroTitle}</h1>
          </header>

          {/* Desktop form - centered inline */}
          <form onSubmit={handleSubmit} className="desktop-form-area">
            <div className="main-input-card">
              <textarea
                ref={inputRef}
                rows={1}
                className="main-input"
                aria-label="Course topic"
                placeholder="e.g., Machine learning fundamentals, Spanish for travel..."
                value={title}
                onChange={handleTitleChange}
                onKeyDown={handleTopicKeyDown}
                disabled={isSubmitting}
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              <div className="main-input-toolbar">
                <button
                  type="button"
                  className={`toolbar-btn ${showAdvanced ? 'is-active' : ''}`}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  aria-expanded={showAdvanced}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="21" x2="4" y2="14" />
                    <line x1="4" y1="10" x2="4" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12" y2="3" />
                    <line x1="20" y1="21" x2="20" y2="16" />
                    <line x1="20" y1="12" x2="20" y2="3" />
                    <line x1="1" y1="14" x2="7" y2="14" />
                    <line x1="9" y1="8" x2="15" y2="8" />
                    <line x1="17" y1="16" x2="23" y2="16" />
                  </svg>
                  Advanced
                  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={() => setShowPdfModal(true)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  Attach PDF
                </button>
                <div className="toolbar-spacer" />
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={!title.trim() || isSubmitting}
                  aria-label="Create course"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={`advanced-content ${showAdvanced ? 'open' : ''}`}>
                <div className="advanced-grid">
                  <div className="field-group">
                    <label className="field-label" htmlFor="difficulty">Difficulty</label>
                    <div className="difficulty-dropdown" ref={difficultyRefDesktop}>
                      <button
                        type="button"
                        id="difficulty"
                        className={`difficulty-trigger ${difficultyOpen ? 'open' : ''}`}
                        onClick={() => setDifficultyOpen(!difficultyOpen)}
                        aria-haspopup="listbox"
                        aria-expanded={difficultyOpen}
                      >
                        <span>{selectedDifficulty?.label || 'Auto-detect'}</span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          style={{
                            opacity: 0.5,
                            transition: 'transform 0.2s ease',
                            transform: difficultyOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        >
                          <path d="M7 10l5 5 5-5z" />
                        </svg>
                      </button>
                      <div className={`difficulty-menu ${difficultyOpen ? 'open' : ''}`} role="listbox">
                        {DIFFICULTIES.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={difficulty === opt.value}
                            className={`difficulty-option ${difficulty === opt.value ? 'selected' : ''}`}
                            onClick={() => {
                              setDifficulty(opt.value);
                              setDifficultyOpen(false);
                            }}
                          >
                            <span className="difficulty-check">
                              {difficulty === opt.value && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </span>
                            <span>{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="field-group">
                    <label className="field-label" htmlFor="courseDomain">Course domain</label>
                    <input
                      type="text"
                      id="courseDomain"
                      className="field-input"
                      placeholder="e.g., Python web development, financial modeling, organic vegetable farming"
                      value={courseDomain}
                      onChange={(e) => setCourseDomain(e.target.value)}
                    />
                  </div>

                  <div className="field-group">
                    <label className="field-label" htmlFor="expectedOutcomes">Expected outcomes</label>
                    <textarea
                      id="expectedOutcomes"
                      className="field-textarea"
                      placeholder="e.g., Build a REST API, create a cash flow projection, design a crop rotation plan"
                      value={expectedOutcomes}
                      onChange={(e) => setExpectedOutcomes(e.target.value)}
                      rows={2}
                      maxLength={1500}
                    />
                    <div className={`field-char-count ${expectedOutcomes.length > 1350 ? 'warning' : ''} ${expectedOutcomes.length >= 1500 ? 'limit' : ''}`}>
                      {expectedOutcomes.length}/1500
                    </div>
                  </div>

                  <div className="field-group">
                    <label className="field-label" htmlFor="prerequisiteKnowledge">Prerequisite knowledge</label>
                    <textarea
                      id="prerequisiteKnowledge"
                      className="field-textarea"
                      placeholder="e.g., Basic programming, algebra, familiarity with Excel"
                      value={prerequisiteKnowledge}
                      onChange={(e) => setPrerequisiteKnowledge(e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              </div>
          </form>
        </div>
      </div>

      {/* Mobile bottom-anchored input */}
      <form onSubmit={handleSubmit} className="mobile-bottom-input">
        <div className={`advanced-content ${showAdvanced ? 'open' : ''}`}>
            <div className="advanced-grid">
              <div className="field-group">
                <label className="field-label" htmlFor="difficulty-mobile">Difficulty</label>
                <div className="difficulty-dropdown" ref={difficultyRefMobile}>
                  <button
                    type="button"
                    id="difficulty-mobile"
                    className={`difficulty-trigger ${difficultyOpen ? 'open' : ''}`}
                    onClick={() => setDifficultyOpen(!difficultyOpen)}
                    aria-haspopup="listbox"
                    aria-expanded={difficultyOpen}
                  >
                    <span>{selectedDifficulty?.label || 'Auto-detect'}</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      style={{
                        opacity: 0.5,
                        transition: 'transform 0.2s ease',
                        transform: difficultyOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}
                    >
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  </button>
                  <div className={`difficulty-menu ${difficultyOpen ? 'open' : ''}`} role="listbox">
                    {DIFFICULTIES.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        role="option"
                        aria-selected={difficulty === opt.value}
                        className={`difficulty-option ${difficulty === opt.value ? 'selected' : ''}`}
                        onClick={() => {
                          setDifficulty(opt.value);
                          setDifficultyOpen(false);
                        }}
                      >
                        <span className="difficulty-check">
                          {difficulty === opt.value && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </span>
                        <span>{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="field-group">
                <label className="field-label" htmlFor="courseDomain-mobile">Course domain</label>
                <input
                  type="text"
                  id="courseDomain-mobile"
                  className="field-input"
                  placeholder="e.g., Python web development"
                  value={courseDomain}
                  onChange={(e) => setCourseDomain(e.target.value)}
                />
              </div>

              <div className="field-group">
                <label className="field-label" htmlFor="expectedOutcomes-mobile">Expected outcomes</label>
                <textarea
                  id="expectedOutcomes-mobile"
                  className="field-textarea"
                  placeholder="e.g., Build a REST API"
                  value={expectedOutcomes}
                  onChange={(e) => setExpectedOutcomes(e.target.value)}
                  rows={2}
                  maxLength={1500}
                />
                <div className={`field-char-count ${expectedOutcomes.length > 1350 ? 'warning' : ''} ${expectedOutcomes.length >= 1500 ? 'limit' : ''}`}>
                  {expectedOutcomes.length}/1500
                </div>
              </div>

              <div className="field-group">
                <label className="field-label" htmlFor="prerequisiteKnowledge-mobile">Prerequisite knowledge</label>
                <textarea
                  id="prerequisiteKnowledge-mobile"
                  className="field-textarea"
                  placeholder="e.g., Basic programming"
                  value={prerequisiteKnowledge}
                  onChange={(e) => setPrerequisiteKnowledge(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>

        <div className="main-input-card">
          <textarea
            rows={1}
            className="main-input"
            aria-label="Course topic"
            placeholder="e.g., Machine learning fundamentals..."
            value={title}
            onChange={handleTitleChange}
            onKeyDown={handleTopicKeyDown}
            disabled={isSubmitting}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          <div className="main-input-toolbar">
            <button
              type="button"
              className={`toolbar-btn ${showAdvanced ? 'is-active' : ''}`}
              onClick={() => setShowAdvanced(!showAdvanced)}
              aria-expanded={showAdvanced}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="21" x2="4" y2="14" />
                <line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" />
                <line x1="20" y1="12" x2="20" y2="3" />
                <line x1="1" y1="14" x2="7" y2="14" />
                <line x1="9" y1="8" x2="15" y2="8" />
                <line x1="17" y1="16" x2="23" y2="16" />
              </svg>
              Advanced
              <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <button
              type="button"
              className="toolbar-btn"
              onClick={() => setShowPdfModal(true)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              PDF
            </button>
            <div className="toolbar-spacer" />
            <button
              type="submit"
              className="submit-btn"
              disabled={!title.trim() || isSubmitting}
              aria-label="Create course"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>
      </form>

      {/* PDF Upload Modal */}
      <PdfUploadModal
        isOpen={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        studentId={studentId}
        onSuccessNavigate={onPdfSuccessNavigate}
        onError={onPdfError}
      />

      {status !== 'idle' && (
        <StatusModal
          status={status}
          errorMessage={errorMessage}
          onRetry={onRetry}
          onDismiss={onDismiss}
          stages={stages}
          onCancel={onCancel}
          successSubtitle={successSubtitle}
          connectionLostHref={connectionLostHref}
          connectionLostLabel={connectionLostLabel}
        />
      )}
    </>
  );
}
